import { createTheme, ThemeProvider } from "@mui/material/styles";
import { DataGrid } from "@mui/x-data-grid";
import { ptBR } from "@mui/x-data-grid/locales";
import { useEffect, useState } from "react";
import { CSVLink } from "react-csv";
import { TbCsv } from "react-icons/tb";
import { Link } from "react-router-dom";
import { SituacaoChamado } from "../../enums/enums";
import CmxInput from "../ui/Input";

import "./style.css";

const theme = createTheme(
  {
    palette: {
      primary: { main: "#1976d2" },
      secondary: { main: "#dc004e" },
    },
    components: {
      MuiDataGrid: {
        styleOverrides: {
          footerContainer: {
            backgroundColor: "var(--azul-600)",
            borderBottomLeftRadius: "6px",
            borderBottomRightRadius: "6px",
            color: "var(--branco)", // cor do texto
            "& .MuiTablePagination-root": {
              color: "var(--branco)", // cor do texto de paginação
            },
            "& .MuiTablePagination-toolbar": {
              color: "var(--branco)", // cor do texto de paginação
            },
            "& .MuiIconButton-root": {
              color: "var(--branco)", // cor dos ícones de paginação
            },
            "& .MuiIconButton-root.Mui-disabled": {
              color: "var(--azul-300)", // cor dos ícones de paginação desabilitados (cinza claro)
            },
            "& .MuiSelect-root": {
              color: "var(--branco)", // cor do seletor de paginação
            },
            "& .MuiSelect-icon": {
              color: "var(--branco)", // cor do ícone do seletor de paginação
            },
            "& .MuiMenuItem-root": {
              color: "var(--branco)", // cor dos itens do menu de paginação
            },
          },
        },
      },
    },
  },
  ptBR
);

const GridChamados = ({ chamados }) => {
  const [filterText, setFilterText] = useState("");
  const [filteredRows, setFilteredRows] = useState(chamados);

  useEffect(() => {
    if (!Array.isArray(chamados)) {
      setFilteredRows([]);
      return;
    }

    const filtered = chamados.filter((row) => {
      const chamadoId = String(row.id || "").toLowerCase();
      const situacao = String(row.situacao_descricao || "").toLowerCase();
      const assunto = String(row.assunto || "").toLowerCase();
      const profissionais = String(row.profissionais || "").toLowerCase();
      const responsavelTi = String(
        row.cliente_usuario_ti?.nome || ""
      ).toLowerCase();
      const rotina = String(row.rotina || "").toLowerCase();
      const observacaoBaixa = String(row.solucao_baixa || "").toLowerCase();

      return (
        chamadoId.includes(filterText) ||
        situacao.includes(filterText) ||
        assunto.includes(filterText) ||
        profissionais.includes(filterText) ||
        responsavelTi.includes(filterText) ||
        rotina.includes(filterText) ||
        observacaoBaixa.includes(filterText)
      );
    });

    setFilteredRows(filtered);
  }, [chamados, filterText]);

  const handleFilterChange = (event) => {
    setFilterText(event.target.value.toLowerCase());
  };

  const csvData = Array.isArray(filteredRows)
    ? filteredRows.map((row) => ({
        Chamado: row.id,
        "Data Entrada": row.data
          ? new Date(row.data).toLocaleDateString("pt-BR")
          : "",
        Assunto: row.assunto,
        Situação: row.situacao_descricao || "",
        "Fora de Garantia": row.fora_de_garantia ? "Sim" : "Não",
        Profissionais: row.profissionais || "",
        "Responsável TI": row.cliente_usuario_ti?.nome || "",
        Previsão: row.previsao_maxima
          ? new Date(row.previsao_maxima).toLocaleDateString("pt-BR")
          : "",
        Rotina: row.rotina || "",
        Encerramento: row.data_encerramento
          ? new Date(row.data_encerramento).toLocaleDateString("pt-BR")
          : "",
        Solução: row.solucao_baixa || "",
      }))
    : [];

  const RedCell = ({ value }) => {
    return <div className="custom-red-cell">{value}</div>;
  };

  const YellowCell = ({ value }) => (
    <div className="custom-yellow-cell">{value}</div>
  );

  const columns = [
    {
      field: "id",
      headerName: "Chamado",
      width: 100,
      renderCell: (params) => (
        <Link
          to={`${params.value}`}
          style={{ color: "#1976d2", textDecoration: "underline" }}
        >
          {params.value}
        </Link>
      ),
    },
    {
      field: "data",
      headerName: "Data Entrada",
      width: 150,
      valueGetter: (params) => {
        return params ? new Date(params).toLocaleDateString("pt-BR") : "";
      },
    },
    { field: "assunto", headerName: "Assunto", width: 250 },
    {
      field: "servico_produto",
      headerName: "Serviço/Produto",
      width: 200,
      valueGetter: (params, row) => {
        return row.servico_produto?.descricao || "";
      },
    },
    {
      // A descrição vem pronta do back (situacao_descricao, resolvida pela tabela chamado_situacao);
      // o destaque colorido continua decidido pelo ID da situação, não pelo texto — renomear
      // "Aguardando Aprovação" no cadastro não pode apagar a cor.
      field: "situacao",
      headerName: "Situação",
      width: 150,
      getCellClassName: "red-cell",
      valueGetter: (params, row) => {
        return row.situacao_descricao || "Desconhecido";
      },
      renderCell: (params) => {
        if (params.row.situacao === SituacaoChamado.Aguardando_Aprovacao) {
          return params.row.aprovado_pelo_portal ? (
            <YellowCell value={params.value} />
          ) : (
            <RedCell value={params.value} />
          );
        }
        return params.value;
      },
    },
    {
      field: "fora_de_garantia",
      headerName: "Fora de Garantia",
      width: 120,
      valueGetter: (params) => {
        return params ? "Sim" : "Não";
      },
    },
    {
      field: "profissionais",
      headerName: "Profissionais",
      width: 200,
      valueGetter: (params) => {
        return params || "";
      },
    },
    {
      field: "cliente_usuario_ti",
      headerName: "Responsável TI",
      width: 150,
      valueGetter: (params, row) => {
        return row.cliente_usuario_ti?.nome || "";
      },
    },
    {
      field: "previsao_maxima",
      headerName: "Previsão",
      width: 120,
      valueGetter: (params) => {
        return params ? new Date(params).toLocaleDateString("pt-BR") : "";
      },
    },
    {
      field: "rotina",
      headerName: "Rotina",
      width: 150,
      valueGetter: (params) => {
        return params || "";
      },
    },
    {
      field: "data_encerramento",
      headerName: "Encerramento",
      width: 120,
      valueGetter: (params) => {
        return params ? new Date(params).toLocaleDateString("pt-BR") : "";
      },
    },
    {
      // Coluna do banco ainda se chama solucao_baixa (legado do Lizard), mas o que ela guarda é a
      // Solução do chamado — mesmo nome que o Raptor usa na tela e no modal de encerramento.
      field: "solucao_baixa",
      headerName: "Solução",
      width: 200,
      valueGetter: (params) => {
        return params || "";
      },
    },
  ];

  return (
    <div style={{ width: "100%", height: "calc(100vh - 245px)" }}>
      <ThemeProvider theme={theme}>
        <DataGrid
          rows={filteredRows}
          columns={columns}
          pageSize={5}
          rowHeight={40}
          headerHeight={40}
          columnHeaderHeight={45}
        />
      </ThemeProvider>
      <div className="custom-input-footer">
        <CmxInput
          type="text"
          placeholder="Localizar..."
          autoFocus={true}
          value={filterText}
          onChange={handleFilterChange}
        />
      </div>

      <div className="custom-csvlink-footer">
        <CSVLink
          data={csvData}
          filename={"chamados.csv"}
          className="btn btn-primary"
          style={{ marginLeft: "10px" }}
        >
          <TbCsv title="Exportar Lista em CSV" size={20} className="csv-icon" />
        </CSVLink>
      </div>
    </div>
  );
};

export default GridChamados;
